import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Chip,
} from '@mui/material';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import api from '../../utils/api';
import MemberProfileAvatar from '../../components/MemberProfileAvatar';
import PageHeader from '../../components/Layout/PageHeader';
import { surfaceCardSx, tokens } from '../../theme/tokens';

export default function AdminVerifyMember() {
  const [memberId, setMemberId] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleVerify = async (e) => {
    e.preventDefault();
    if (!memberId?.trim()) return;
    setLoading(true);
    setError('');
    setData(null);
    try {
      const res = await api.get(`/member-portal/verify/${memberId.trim()}`);
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Verification failed');
      setData({ verified: false });
    } finally {
      setLoading(false);
    }
  };

  const verified = data?.verified === true;
  const member = data?.member;

  return (
    <Box>
      <PageHeader
        crumbs={
          <>
            Registry / <Box component="b" sx={{ color: tokens.ink, fontWeight: 500 }}>Verify Member</Box>
          </>
        }
        title="Verify Member"
      />
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, mt: -1.5 }}>
        Enter a member ID to verify membership, or scan the QR code on a member&apos;s ID card with your phone.
      </Typography>

      <Paper elevation={0} sx={{ ...surfaceCardSx, p: 3, mb: 3 }}>
        <Typography variant="subtitle2" gutterBottom>
          Verify by Member ID
        </Typography>
        <form onSubmit={handleVerify}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <TextField
              label="Member ID"
              value={memberId}
              onChange={(e) => setMemberId(e.target.value)}
              placeholder="Member ID or Association Number"
              size="small"
              sx={{ minWidth: 200 }}
            />
            <Button type="submit" variant="contained" disabled={loading}>
              {loading ? <CircularProgress size={24} /> : 'Verify'}
            </Button>
          </Box>
        </form>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {data && (
        <Paper elevation={0} sx={{ ...surfaceCardSx, p: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Verification Result
          </Typography>
          {verified ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 2 }}>
              <CheckCircleIcon sx={{ fontSize: 48, color: 'success.main' }} />
              <Typography variant="h6">
                {[member?.first_name, member?.last_name].filter(Boolean).join(' ') || 'Member'}
              </Typography>
              <MemberProfileAvatar member={member} size={64} />
              <Typography variant="body2" color="text.secondary">
                ID: {member?.member_id || member?.association_number || '—'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {member?.institution || '—'}
              </Typography>
              <Chip label={member?.status || 'Active'} color="success" size="small" />
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, py: 2 }}>
              <CancelIcon sx={{ fontSize: 48, color: 'error.main' }} />
              <Typography color="error">
                {data?.message || 'Member not verified'}
              </Typography>
            </Box>
          )}
        </Paper>
      )}

      <Paper elevation={0} sx={{ ...surfaceCardSx, p: 2, mt: 3, bgcolor: tokens.primarySoft }}>
        <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <QrCodeScannerIcon fontSize="small" />
          Scanning the QR code
        </Typography>
        <Typography variant="body2" color="text.secondary">
          When you scan the QR code on a member&apos;s ID card with your phone camera, it opens a verification page
          showing the member&apos;s name, photo, and status. No login required—executives and admin can verify
          members on the spot.
        </Typography>
      </Paper>
    </Box>
  );
}
